/**
 * AI Agent Service
 *
 * Service for managing AI agents with RAG (Retrieval Augmented Generation) capabilities.
 * Provides CRUD operations for agents, knowledge management, and chat functionality.
 *
 * API endpoints are proxied through the backend at /api/v1/wes-ai/*
 */

import apiClient from './apiClient';

const WES_AI_BASE = '/wes-ai';
const AGENTS_CACHE_KEY = 'hub360:wes-ai:agents-cache:v1';
const FALLBACK_TEXT_AGENTS = [
  {
    id: 'demo-agent-recovery',
    name: 'Agente Demo Recuperacao',
    description: 'Fallback local para campanhas de recuperacao.',
    is_voice_agent: false,
  },
  {
    id: 'demo-agent-followup',
    name: 'Agente Demo Follow-up',
    description: 'Fallback local para campanhas de follow-up.',
    is_voice_agent: false,
  },
  {
    id: 'demo-agent-feedback',
    name: 'Agente Demo Pos-venda',
    description: 'Fallback local para campanhas de pos-venda.',
    is_voice_agent: false,
  },
];

function canUseLocalStorage() {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

function readCachedAgents() {
  if (!canUseLocalStorage()) return null;
  try {
    const raw = window.localStorage.getItem(AGENTS_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function writeCachedAgents(agents) {
  if (!canUseLocalStorage() || !Array.isArray(agents)) return;
  try {
    window.localStorage.setItem(AGENTS_CACHE_KEY, JSON.stringify(agents));
  } catch {
    // Ignore storage write errors (quota/private mode) and keep normal flow.
  }
}

// wes-ai returns `environment_id/environment_name/environment_slug/
// membership_role` but WesScopeContext, ProjectFormDialog, and
// AIAgentsPage all read the shorter `id/name/slug/role`. Alias both
// shapes so callers using either naming continue to work. We never
// drop the originals — anyone still reading `environment_*` keeps
// seeing the raw values.
function _normalizeEnvironment(raw) {
  if (!raw || typeof raw !== 'object') return raw;
  return {
    id: raw.environment_id ?? raw.id ?? null,
    name: raw.environment_name ?? raw.name ?? null,
    slug: raw.environment_slug ?? raw.slug ?? null,
    role: raw.membership_role ?? raw.role ?? null,
    ...raw,
  };
}

class AIAgentService {
  /**
   * List all AI agents
   * @returns {Promise<Array>} List of agents
   */
  async getAgents() {
    try {
      const response = await apiClient.get(`${WES_AI_BASE}/agents`);
      const list = Array.isArray(response.data) ? response.data : [];
      writeCachedAgents(list);
      return list;
    } catch (error) {
      // wes-ai can be temporarily unavailable behind the gateway (502/503).
      // Reuse the last successful roster so dependent screens keep working.
      const status = error?.response?.status;
      const isTransientGatewayError = [502, 503, 504].includes(status);
      const isServerError = typeof status === 'number' && status >= 500;
      const isNetworkError = !error?.response;
      if (isTransientGatewayError || isServerError || isNetworkError) {
        const cached = readCachedAgents();
        if (cached && cached.length > 0) {
          return cached;
        }
        return FALLBACK_TEXT_AGENTS;
      }
      throw error;
    }
  }

  /**
   * Get a single AI agent by ID
   * @param {string} agentId - The agent ID
   * @returns {Promise<Object>} Agent details
   */
  async getAgent(agentId) {
    const response = await apiClient.get(`${WES_AI_BASE}/agents/${agentId}`);
    return response.data;
  }

  /**
   * Create a new AI agent
   * @param {Object} agentData - Agent creation data
   * @param {string} agentData.name - Agent name (required)
   * @param {string} agentData.description - Agent description (optional)
   * @param {string} agentData.system_prompt - System prompt (required)
   * @param {boolean} agentData.use_rag - Enable RAG (default: true)
   * @param {boolean} [agentData.is_voice_agent] - Hub-only voice flag
   * @param {string} [agentData.voice_locale] - e.g. pt-BR
   * @param {string} [agentData.voice_name] - e.g. pt-BR-FranciscaNeural
   * @param {Object} [agentData.voice_capture_config] - Hub-only audio/capture JSON
   * @returns {Promise<Object>} Created agent
   */
  async createAgent(agentData) {
    const response = await apiClient.post(`${WES_AI_BASE}/agents`, agentData);
    return response.data;
  }

  /**
   * Update an existing AI agent
   * @param {string} agentId - The agent ID
   * @param {Object} agentData - Agent update data
   * @returns {Promise<Object>} Updated agent
   */
  async updateAgent(agentId, agentData) {
    // Hub backend exposes PATCH (not PUT) on /wes-ai/agents/{id}; wes-ai
    // upstream is also PATCH-only. PUT would 405 here.
    const response = await apiClient.patch(`${WES_AI_BASE}/agents/${agentId}`, agentData);
    return response.data;
  }

  /**
   * Delete an AI agent
   * @param {string} agentId - The agent ID
   * @param {boolean} deleteKb - Whether to delete the knowledge base (default: true)
   * @returns {Promise<void>}
   */
  async deleteAgent(agentId, deleteKb = true) {
    await apiClient.delete(`${WES_AI_BASE}/agents/${agentId}`, {
      params: { delete_kb: deleteKb }
    });
  }

  /**
   * Ingest text into agent's knowledge base
   * @param {string} agentId - The agent ID
   * @param {string} text - Text content to ingest
   * @param {string} source - Optional source identifier
   * @returns {Promise<Object>} Ingestion result
   */
  async ingestText(agentId, text, source = null) {
    const payload = { text };
    if (source) {
      payload.source = source;
    }
    const response = await apiClient.post(
      `${WES_AI_BASE}/agents/${agentId}/knowledge/ingest_text`,
      payload
    );
    return response.data;
  }

  /**
   * Ingest file into agent's knowledge base
   * @param {string} agentId - The agent ID
   * @param {File} file - File to ingest
   * @param {string} source - Optional source identifier
   * @param {Function} onProgress - Optional progress callback
   * @returns {Promise<Object>} Ingestion result
   */
  async ingestFile(agentId, file, source = null, onProgress = null) {
    const formData = new FormData();
    formData.append('file', file);

    let url = `${WES_AI_BASE}/agents/${agentId}/knowledge/ingest_file`;
    if (source) {
      url += `?source=${encodeURIComponent(source)}`;
    }

    const config = {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    };

    if (onProgress) {
      config.onUploadProgress = (progressEvent) => {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        onProgress(percentCompleted);
      };
    }

    const response = await apiClient.post(url, formData, config);
    return response.data;
  }

  /**
   * Chat with an AI agent
   * @param {string} agentId - The agent ID
   * @param {Array<Object>} messages - Chat messages
   * @param {Object} options - Optional chat parameters
   * @param {number} options.top_k - Number of context chunks (1-50)
   * @param {number} options.temperature - Temperature (0-2)
   * @param {boolean} options.use_context - Whether to use RAG context
   * @param {string} options.conversationId - wes-ai conversation id. When
   *   present, the turn is appended to that thread. When omitted, wes-ai
   *   creates a new conversation and returns its id on the response so the
   *   caller can keep the thread alive across turns.
   * @returns {Promise<Object>} Chat response: { agent_id, answer, citations,
   *   conversation_id }
   */
  async chatWithAgent(agentId, messages, options = {}) {
    const payload = { messages };

    if (options.top_k !== undefined) {
      payload.top_k = options.top_k;
    }
    if (options.temperature !== undefined) {
      payload.temperature = options.temperature;
    }
    if (options.use_context !== undefined) {
      payload.use_context = options.use_context;
    }
    if (options.conversationId) {
      payload.conversation_id = options.conversationId;
    }

    const response = await apiClient.post(
      `${WES_AI_BASE}/agents/${agentId}/chat`,
      payload
    );
    return response.data;
  }

  /**
   * List conversations for the current user, optionally scoped to an agent.
   * @param {Object} options
   * @param {string} options.agentId - Filter by agent id
   * @param {string} options.type - Filter by conversation type (direct|agent|summary)
   * @param {number} options.limit - Default 50, max 200
   * @param {number} options.offset - Default 0
   * @returns {Promise<Array>} List of conversation summaries
   */
  async listConversations(options = {}) {
    const params = {};
    if (options.agentId) params.agent_id = options.agentId;
    if (options.type) params.type = options.type;
    if (options.limit !== undefined) params.limit = options.limit;
    if (options.offset !== undefined) params.offset = options.offset;
    const response = await apiClient.get(`${WES_AI_BASE}/conversations`, { params });
    return response.data;
  }

  /**
   * Get a conversation's metadata.
   */
  async getConversation(conversationId) {
    const response = await apiClient.get(`${WES_AI_BASE}/conversations/${conversationId}`);
    return response.data;
  }

  /**
   * List messages of a conversation, oldest first.
   */
  async listConversationMessages(conversationId, { limit, offset } = {}) {
    const params = {};
    if (limit !== undefined) params.limit = limit;
    if (offset !== undefined) params.offset = offset;
    const response = await apiClient.get(
      `${WES_AI_BASE}/conversations/${conversationId}/messages`,
      { params }
    );
    return response.data;
  }

  /**
   * Rename a conversation.
   */
  async renameConversation(conversationId, title) {
    const response = await apiClient.patch(
      `${WES_AI_BASE}/conversations/${conversationId}`,
      { title }
    );
    return response.data;
  }

  /**
   * Hide a conversation (soft by default; pass hard=true to remove permanently).
   */
  async deleteConversation(conversationId, { hard = false } = {}) {
    await apiClient.delete(`${WES_AI_BASE}/conversations/${conversationId}`, {
      params: { hard }
    });
  }

  /**
   * List wes-ai environments the current user has access to.
   *
   * Normalised so callers can read either the canonical short fields
   * (`id`, `name`, `slug`, `role`) or the raw wes-ai fields
   * (`environment_id`, `environment_name`, `environment_slug`,
   * `membership_role`) — both are present on every entry.
   *
   * @returns {Promise<Array>}
   */
  async listEnvironments() {
    const response = await apiClient.get(`${WES_AI_BASE}/environments`);
    return Array.isArray(response.data)
      ? response.data.map(_normalizeEnvironment)
      : [];
  }

  /**
   * Create a new wes-ai environment. The caller becomes the owner.
   * Response shape matches listEnvironments entries (canonical + raw
   * fields aliased).
   *
   * @param {Object} payload
   * @param {string} payload.name    Human label, 1-200 chars (required)
   * @param {string} payload.slug    URL identifier, 1-120 chars (required)
   * @param {Array<{user_id?: string, email?: string, role?: string}>} [payload.members]
   * @returns {Promise<Object>}
   */
  async createEnvironment(payload) {
    const response = await apiClient.post(
      `${WES_AI_BASE}/environments`,
      payload,
    );
    return _normalizeEnvironment(response.data);
  }

  /**
   * List wes-ai projects across all of the user's environments.
   * Each entry: { project_id, project_slug, project_title, description,
   *               environment_id, environment_name, environment_slug,
   *               agent_count }
   * @returns {Promise<Array>}
   */
  async listProjects() {
    const response = await apiClient.get(`${WES_AI_BASE}/projects`);
    return response.data;
  }

  /**
   * Create a project inside an environment.
   * @param {Object} payload
   * @param {string} payload.environment_slug  - Required; target environment
   * @param {string} payload.title             - Visible name
   * @param {string} payload.description       - Required by wes-ai
   * @param {string[]} [payload.agent_ids]     - Optional initial agents
   * @returns {Promise<Object>} Created project
   */
  async createProject(payload) {
    const response = await apiClient.post(`${WES_AI_BASE}/projects`, payload);
    return response.data;
  }

  /**
   * Partially update a project. Only fields present in the payload are
   * sent; omit one to keep the stored value.
   * @param {string} projectId
   * @param {Object} payload
   * @returns {Promise<Object>} Updated project
   */
  async updateProject(projectId, payload) {
    const response = await apiClient.patch(
      `${WES_AI_BASE}/projects/${projectId}`,
      payload
    );
    return response.data;
  }

  /**
   * Delete a project. Cascades ACL rows; agents are kept.
   * @param {string} projectId
   * @returns {Promise<void>}
   */
  async deleteProject(projectId) {
    await apiClient.delete(`${WES_AI_BASE}/projects/${projectId}`);
  }

  /**
   * List every wes-ai skill (catalogue, not per-agent).
   * Each entry: { id, skill, descricao, input_schema, habilitada }
   * @returns {Promise<Array>}
   */
  async listSkills() {
    const response = await apiClient.get(`${WES_AI_BASE}/skills`);
    return Array.isArray(response.data) ? response.data : [];
  }

  /**
   * Administrative catalog: Hub360-owned skills merged with wes-ai catalogue.
   * Each entry: { id, skill, description, enabled, source, file_name, editable, updated_at }
   */
  async listAdminSkills() {
    const response = await apiClient.get(`${WES_AI_BASE}/admin/skills`);
    return Array.isArray(response.data) ? response.data : [];
  }

  /**
   * Create a Hub360-owned skill with validated .zip / .md upload.
   */
  async createAdminSkill({ name, description, enabled, file }) {
    const formData = new FormData();
    formData.append('name', name);
    formData.append('description', description ?? '');
    formData.append('enabled', enabled ? 'true' : 'false');
    formData.append('file', file);
    const response = await apiClient.post(`${WES_AI_BASE}/admin/skills`, formData);
    return response.data;
  }

  async updateSkill(skillId, payload) {
    const response = await apiClient.patch(`${WES_AI_BASE}/skills/${skillId}`, payload);
    return response.data;
  }

  async deleteSkill(skillId) {
    await apiClient.delete(`${WES_AI_BASE}/skills/${skillId}`);
  }

  /**
   * Per-agent skill state (which skills this specific agent has
   * enabled). Each entry: { agent_id, skill, enabled }.
   * @returns {Promise<Array>}
   */
  async listAgentSkills(agentId) {
    const response = await apiClient.get(
      `${WES_AI_BASE}/agents/${agentId}/skills`,
    );
    return Array.isArray(response.data) ? response.data : [];
  }

  /**
   * Toggle a single skill on/off for an agent.
   * Backend accepts a list of updates; we always send one at a time
   * for simpler call-sites. Returns the updated skill roster.
   */
  async updateAgentSkill(agentId, skill, enabled) {
    const response = await apiClient.post(
      `${WES_AI_BASE}/agents/${agentId}/skills`,
      [{ skill, enabled }],
    );
    return Array.isArray(response.data) ? response.data : [];
  }

  /**
   * Rename a conversation.
   */
  async renameConversation(conversationId, title) {
    const response = await apiClient.patch(
      `${WES_AI_BASE}/conversations/${conversationId}`,
      { title }
    );
    return response.data;
  }

  /**
   * Hide a conversation (soft by default; pass hard=true to remove permanently).
   */
  async deleteConversation(conversationId, { hard = false } = {}) {
    await apiClient.delete(`${WES_AI_BASE}/conversations/${conversationId}`, {
      params: { hard }
    });
  }

  /**
   * List wes-ai environments the current user has access to.
   * Each entry: { id, name, slug, role, created_at }
   * @returns {Promise<Array>}
   */
  async listEnvironments() {
    const response = await apiClient.get(`${WES_AI_BASE}/environments`);
    return response.data;
  }

  /**
   * List wes-ai projects across all of the user's environments.
   * Each entry: { project_id, project_slug, project_title, description,
   *               environment_id, environment_name, environment_slug,
   *               agent_count }
   * @returns {Promise<Array>}
   */
  async listProjects() {
    const response = await apiClient.get(`${WES_AI_BASE}/projects`);
    return response.data;
  }

  /**
   * Create a project inside an environment.
   * @param {Object} payload
   * @param {string} payload.environment_slug  - Required; target environment
   * @param {string} payload.title             - Visible name
   * @param {string} payload.description       - Required by wes-ai
   * @param {string[]} [payload.agent_ids]     - Optional initial agents
   * @returns {Promise<Object>} Created project
   */
  async createProject(payload) {
    const response = await apiClient.post(`${WES_AI_BASE}/projects`, payload);
    return response.data;
  }

  /**
   * Partially update a project. Only fields present in the payload are
   * sent; omit one to keep the stored value.
   * @param {string} projectId
   * @param {Object} payload
   * @returns {Promise<Object>} Updated project
   */
  async updateProject(projectId, payload) {
    const response = await apiClient.patch(
      `${WES_AI_BASE}/projects/${projectId}`,
      payload
    );
    return response.data;
  }

  /**
   * Delete a project. Cascades ACL rows; agents are kept.
   * @param {string} projectId
   * @returns {Promise<void>}
   */
  async deleteProject(projectId) {
    await apiClient.delete(`${WES_AI_BASE}/projects/${projectId}`);
  }

  /**
   * Check wes-ai service health
   * @returns {Promise<Object>} Health status
   */
  async checkHealth() {
    const response = await apiClient.get(`${WES_AI_BASE}/health`);
    return response.data;
  }

  /**
   * Publish an agent for external access.
   *
   * Idempotent: when the agent already has an active publication the
   * backend returns the same slug with ``token: null``. The raw token
   * is returned EXACTLY once on the initial publish and must be
   * surfaced to the admin immediately — it is not retrievable later.
   *
   * @param {string} agentId - wes-ai agent id
   * @param {{ modality?: 'text' | 'voice' }} options
   * @returns {Promise<{
   *   publication_id: string,
   *   slug: string,
   *   public_url: string,
   *   status: 'active' | 'revoked',
   *   modality: 'text' | 'voice',
   *   published_at: string,
   *   revoked_at: string | null,
   *   token: string | null,
   * }>}
   */
  async publishAgent(agentId, { modality = 'text' } = {}) {
    const response = await apiClient.post(
      `${WES_AI_BASE}/agents/${agentId}/publish`,
      { modality }
    );
    return response.data;
  }

  /**
   * Revoke the active publication for ``agentId``.
   *
   * Idempotent: calling it on a non-published agent still returns 204.
   * After this call the external URL stops working immediately.
   *
   * @param {string} agentId
   * @returns {Promise<void>}
   */
  async unpublishAgent(agentId) {
    await apiClient.post(`${WES_AI_BASE}/agents/${agentId}/unpublish`);
  }

  /**
   * Fetch the active publication metadata for ``agentId``.
   *
   * Returns ``null`` when the agent is not currently published
   * (backend returns 404, which is the canonical "not published"
   * signal — we do NOT surface that as an error).
   *
   * Never returns the raw token. Regenerating the token requires an
   * explicit unpublish + publish cycle.
   *
   * @param {string} agentId
   * @returns {Promise<Object | null>}
   */
  async getAgentPublication(agentId) {
    try {
      const response = await apiClient.get(
        `${WES_AI_BASE}/agents/${agentId}/publication`
      );
      return response.data;
    } catch (err) {
      if (err?.response?.status === 404) {
        return null;
      }
      throw err;
    }
  }

  /**
   * Curated voice catalog (Hub intersects Azure live list with allowlist).
   * @param {{ locale?: string, refresh?: boolean }} [params]
   * @returns {Promise<Array<{ locale: string, voices: Array<{ name: string, gender?: string, category?: string }> }>>}
   */
  async listVoiceCatalog(params = {}) {
    const response = await apiClient.get('/voice-catalog', { params });
    return response.data;
  }

  /**
   * Refine um system prompt usando o LLM (via wes-ai). Endpoint chama
   * ``/wes-ai/chat`` direto, não cria/registra agente — é só uma
   * one-shot pra produzir um prompt mais limpo e específico.
   *
   * @param {Object} args
   * @param {string} args.currentPrompt - Prompt atual (não-vazio).
   * @param {string} [args.agentName] - Opcional, dá contexto pro modelo.
   * @param {string} [args.agentDescription] - Opcional, idem.
   * @returns {Promise<{ prompt: string }>}
   */
  async improvePrompt({ currentPrompt, agentName, agentDescription } = {}) {
    const response = await apiClient.post(`${WES_AI_BASE}/prompts/improve`, {
      current_prompt: currentPrompt,
      agent_name: agentName || null,
      agent_description: agentDescription || null,
    });
    return response.data;
  }

  /**
   * Short MP3 sample for a curated voice (POST /voice-catalog/preview, Azure TTS).
   * @param {{ voiceName: string, text?: string }} param
   * @returns {Promise<Blob>}
   */
  async previewVoice({ voiceName, text } = {}) {
    const body = { voice_name: voiceName };
    if (text) body.text = text;
    try {
      const response = await apiClient.post('/voice-catalog/preview', body, {
        responseType: 'blob',
        timeout: 60000
      });
      const b = response.data;
      if (b instanceof Blob && b.type && b.type.includes('json')) {
        const raw = await b.text();
        let detail = 'Voice preview failed';
        try {
          const j = JSON.parse(raw);
          if (typeof j.detail === 'string') detail = j.detail;
          else if (Array.isArray(j.detail) && j.detail[0]?.msg) detail = j.detail[0].msg;
        } catch {
          // keep default
        }
        const err = new Error(detail);
        err.response = { data: { detail } };
        throw err;
      }
      return b;
    } catch (err) {
      const data = err?.response?.data;
      if (data instanceof Blob) {
        const raw = await data.text();
        let detail = null;
        try {
          const j = JSON.parse(raw);
          if (typeof j.detail === 'string') detail = j.detail;
          else if (Array.isArray(j.detail) && j.detail[0]?.msg) detail = j.detail[0].msg;
        } catch {
          // ignore
        }
        if (detail) {
          const next = new Error(detail);
          next.response = { data: { detail } };
          throw next;
        }
      }
      throw err;
    }
  }

  /**
   * Hub-only PATCH for is_voice_agent / voice_locale / voice_name.
   * @param {string} agentId - wes-ai agent id
   * @param {{ is_voice_agent?: boolean, voice_locale?: string, voice_name?: string, voice_capture_config?: object }} payload
   */
  async updateAgentVoiceSettings(agentId, payload) {
    const response = await apiClient.patch(`/agent-settings/${agentId}`, payload);
    return response.data;
  }

  /**
   * One-shot ticket for browser WebSocket (proxied to wes-ai).
   * @param {string} agentId
   * @param {{ voice_mode?: 'conversation' | 'dictation' }} [body]
   * @returns {Promise<{ ticket: string, expires_in_seconds: number }>}
   */
  async issueVoiceTicket(agentId, body) {
    const response = await apiClient.post(
      `${WES_AI_BASE}/agents/${agentId}/voice/ticket`,
      body || undefined
    );
    return response.data;
  }
}

const aiAgentService = new AIAgentService();
export default aiAgentService;
